import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Typography } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Dropdown } from '@components/atoms/Dropdown';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Form } from '@components/atoms/Form';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useLogout } from '@services/useLogout';
import styles from './UserProfileDropdown.module.css';

interface UserProfileDropdownProps {
    user?: {
        name?: string;
        lastName?: string;
        roles?: string[];
        email?: string;
        photo?: string;
    };
}

export const UserProfileDropdown: FC<UserProfileDropdownProps> = ({ user }) => {
    const { t } = useTranslation();

    const translations = {
        buttonSalutation: t('user-profile-dropdown-component.salutation'),
        title: t('user-profile-dropdown-component.card.title'),
        myAccount: t('user-profile-dropdown-component.card.account'),
        login: t('user-profile-dropdown-component.login-text'),
        salutation: t('user-profile-dropdown-component.card-logout.salutation'),
        createStart: t('user-profile-dropdown-component.card-logout.create-start'),
        create: t('user-profile-dropdown-component.card-logout.create'),
        start: t('user-profile-dropdown-component.card-logout.start'),
    };

    return (
        <Dropdown
            buttonContent={
                <div className="d-flex align-items-center">
                    <RoundIcon
                        variant={RoundIconVariants.variant1}
                        icon={!user?.photo ? 'fa-light fa-user-hair' : undefined}
                        bgImage={user?.photo}
                    />

                    <Typography classes="ms-1 mb-0 lh-sm d-none d-sm-block text-start">
                        <>
                            {translations.buttonSalutation}
                            <br />
                            {user ? user.name : <small>{translations.login}</small>}
                        </>
                    </Typography>
                </div>
            }
            position="right"
            fullWidthOnMobile={true}>
            <div id={styles['user-profile-dropdown']}>
                <>
                    {user ? (
                        <div className="p-4">
                            <Typography fontSize="20" classes="mb-3">
                                {translations.title}
                            </Typography>
                            <div className="d-flex">
                                {user?.photo ? (
                                    <img className={styles['rounded-circle']} src={user.photo} alt={user.name} />
                                ) : (
                                    <div
                                        className={`d-flex flex-column justify-content-center text-center ${styles['rounded-circle']} bg-secondary`}>
                                        <i className="fa-thin fa-user-hair" />
                                    </div>
                                )}
                                <div className="d-flex flex-column align-self-center row-gap-2 ps-4">
                                    <Typography fontSize="14" classes="m-0">
                                        <>
                                            {user.name} {user.lastName}
                                        </>
                                    </Typography>
                                    <Typography color="muted" fontSize="12" classes="m-0">
                                        {Array.isArray(user.roles) ? user.roles?.join(', ') : ''}
                                    </Typography>
                                    <div className="d-flex">
                                        <Typography fontSize="12" classes="mb-0">
                                            <i className="fa-light fa-envelope me-2 align-middle" />
                                        </Typography>
                                        <Typography fontSize="12" classes="text-break mb-0">
                                            {user.email}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-4 p-4">
                            <Typography fontSize="20">{translations.salutation}</Typography>
                            <Typography fontSize="16" color="muted">
                                {translations.createStart}
                            </Typography>
                        </div>
                    )}
                </>
                <hr className="m-0" />
                <div className="d-grid gap-3 p-4">
                    <>
                        {user ? (
                            <Anchor
                                href={/*can(UserPermissions.accessMerchantAreas) ? SITE_URLS.myDashboard :*/ SITE_URLS.myProfile}
                                mode={AnchorMode.primary}>
                                {translations.myAccount}
                            </Anchor>
                        ) : (
                            <>
                                <Anchor href="login" mode={AnchorMode.primary}>
                                    {translations.start}
                                </Anchor>
                                <Anchor href="register" outline={true} mode={AnchorMode.primary}>
                                    {translations.create}
                                </Anchor>
                            </>
                        )}
                        <LogoutForm show={Boolean(user?.email)} />
                    </>
                </div>
            </div>
        </Dropdown>
    );
};

interface LogoutFormProps {
    show: boolean;
}

const LogoutForm: FC<LogoutFormProps> = ({ show }) => {
    const { navigate } = useCustomNavigate();
    const { t } = useTranslation();
    const { request: requestLogout, loading, lastResponseTime } = useLogout();
    const { login } = useData();

    const translations = {
        logout: t('user-profile-dropdown-component.card.logout'),
    };

    const formRef = useRef<HTMLFormElement>(null);

    const handleOnClickLogout = () => {
        formRef?.current?.requestSubmit();
    };

    const submitForm = () => {
        requestLogout();
    };

    // if logged out successfully, go to homepage
    useEffect(() => {
        if (lastResponseTime !== null && login === null) {
            navigate('/');
            // requestCart({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <>
            {show && (
                <Form formRef={formRef} classes="d-flex flex-column" onSubmit={submitForm}>
                    <Button
                        label={translations.logout}
                        outline={true}
                        mode={ButtonMode.primary}
                        onClick={handleOnClickLogout}
                        disabled={loading}
                    />
                </Form>
            )}
        </>
    );
};
